/**
 * Типы для админского кабинета (AdminOffice)
 */

// ============================================
// СУЩНОСТИ
// ============================================

/**
 * Упрощенная версия клиента для списка результатов поиска
 */
export interface AdminClient {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
  middleName: string;
  passportSeries: string;
  passportNumber: string;
}

/**
 * Сделка (кредитный договор)
 */
export interface Deal {
  id: number;
  number: string;
  clientId: number;
  status: string;
}

/**
 * Полные данные клиента для редактирования в админке
 */
export interface ClientData {
  id: number;
  fullName?: string;
  firstName: string;
  lastName: string;
  middleName: string;
  passportSeries: string;
  passportNumber: string;
  birthDate: string;
  passportIssueDate: string;
  passportIssueAuthority: string;
  passportIssueLocation: string;
  mobilePhone: string;
  email: string;
}

/**
 * Тип документа
 */
export interface DocumentType {
  id: number;
  name: string;
  code?: string;
}

/**
 * Фильтр ставок
 */
export interface RateFilter {
  id: string;
  description: string;
  enabled: boolean;
}

/**
 * Динамическое свойство системы
 */
export interface DynamicProperty {
  key: string;
  value: boolean;
  lastUpdated?: string;
}


// ============================================
// ПАРАМЕТРЫ ЗАПРОСОВ
// ============================================

/**
 * Параметры поиска клиента
 */
export interface ClientSearchParams {
  lastname?: string;
  firstname?: string;
  middlename?: string;
  series?: string;
  number?: string;
}

/**
 * Параметры создания платежа
 */
export interface PaymentParams {
  creditContractId: number;
  sum: number;
  date: string;
}

/**
 * Параметры прямого поступления через р/с
 */
export interface DirectReceiptParams extends PaymentParams {
  account: string;
}

/**
 * Параметры генерации документов
 */
export interface DocumentGenerationParams {
  applicationId: number;
  genDate?: string;
  tosave: boolean;
  sendToMyMfo: boolean;
  docType: number;
}

/**
 * Параметры загрузки файла
 */
export interface FileUploadParams {
  file: File;
  quarter?: string;
  year?: string;
}

// ============================================
// ФОРМЫ (для Formik)
// ============================================

/**
 * Форма реанимации заявки
 */
export interface ReviveApplicationForm {
  applicationId: number | null;
}

/**
 * Форма смены точки продаж
 */
export interface UpdatePointOfSaleForm {
  territorialDirection: number;
  pointOfSale: number;
}

/**
 * Форма создания платежа
 */
export interface CreatePaymentForm {
  sum: number | null;
  date: string | null;
}

/**
 * Форма прямого поступления
 */
export interface DirectReceiptForm extends CreatePaymentForm {
  account: string;
}

/**
 * Форма обновления данных клиента
 */
export interface UpdateClientDataForm {
  id: number;
  firstName: string;
  lastName: string;
  middleName: string;
  birthDate: string;
  passportSeries: string;
  passportNumber: string;
  passportIssueDate: string;
  passportIssueAuthority: string;
  passportIssueLocation: string;
  mobilePhone: string;
  email: string;
}

/**
 * Форма загрузки справочников
 */
export interface FileUploadForm {
  quarter?: string;
  year?: string;
}

/**
 * Форма создания пользователя
 */
export interface CreateUserForm {
  login: string;
  password: string;
  comment: string;
}

/**
 * Форма генерации документов
 */
export interface GenerateDocumentForm {
  applicationId: number | null;
  genDate: string;
  tosave: boolean;
  sendToMyMfo: boolean;
}

/**
 * Форма поиска клиента
 */
export interface ClientSearchForm {
  lastname: string | null;
  firstname: string | null;
  middlename: string | null;
  series: string | null;
  number: string | null;
}

// ============================================
// API RESPONSE ТИПЫ
// ============================================

/**
 * Базовый успешный ответ API
 */
export interface ApiSuccessResponse {
  success: boolean;
  message?: string;
}

/**
 * Ответ на загрузку файла
 */
export interface FileUploadResponse extends ApiSuccessResponse {
  fileName?: string;
  recordsProcessed?: number;
  data?: string;
}

/**
 * Итоги загрузки csv в черный список (person_banned)
 */
export interface PersonBannedUploadResponse {
  total: number;
  added: number;
  duplicates: number;
  errors: string[];
}

/**
 * Пагинированный ответ
 */
export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/**
 * Ответ с данными клиента
 */
export interface ClientDataResponse {
  data: ClientData;
}

// ============================================
// ENUMS
// ============================================

/**
 * Типы загружаемых файлов
 */
export enum FileUploadType {
  AverageIncome = 'averageIncome',
  FullCost = 'fullCost',
  PointOfSale = 'pointOfSale',
  Payments = 'payments',
  PaymentsRs = 'paymentsRs',
  DealTypeMapping = 'dealTypeMapping',
  Registry9FZ = 'registry9fz',
}

/**
 * Ключи динамических свойств
 */
export enum DynamicPropertyKey {
  MPL80 = 'mpl80',
  MPL5080 = 'mpl5080',
  PaymentState = 'paymentState',
}

/**
 * Секции админ-панели
 */
export enum AdminSection {
  Issue = 'issue',
  Repayments = 'repayments',
  EditingClientData = 'editingClientData',
  References = 'references',
  AddUser = 'addUser',
  Other = 'other',
}
